import { Command, Plugin, toWidget } from '../ckeditor-plugins';
import { iteratorToObject } from '../utils';
import { ARBO_EMOJI } from '../constants';

/**
 * Interface for emoji attributes in the model
 */
interface EmojiAttributes extends Record<string, unknown> {
    name: string;
    style?: string;
}

/**
 * Configuration constants for the emoji plugin
 */
const EMOJI_CONFIG = {
    PLUGIN_NAME: 'EmojiPlugin',
    WIDGET_LABEL: 'Emoji widget',
    VIEW_CLASS: 'arbo-emoji',
    ALLOWED_ATTRIBUTES: ['name', 'style'],
} as const;

/**
 * Command for inserting emoji elements into the editor
 */
class EmojiCommand extends Command {
    /**
     * Executes the emoji insertion command
     * @param name - The name/identifier of the emoji to insert
     */
    public execute(name: string): void {
        if (!name?.trim()) {
            console.warn('EmojiCommand: Empty or invalid emoji name provided');
            return;
        }

        const model = this.editor.model;
        
        model.change((writer) => {
            // Create the emoji element with the specified name
            const emojiElement = writer.createElement(ARBO_EMOJI, { name: name.trim() });
            
            // Insert the emoji at the current selection
            model.insertContent(emojiElement);
        });
    }
}

/**
 * Converts model attributes to emoji-specific attributes
 * Filters and validates attributes for emoji elements
 */
function convertToEmojiAttributes(attributes: Record<string, unknown>): EmojiAttributes {
    const result: EmojiAttributes = {
        name: String(attributes.name || ''),
    };

    // Only include style if it exists and is not empty
    if (attributes.style && typeof attributes.style === 'string') {
        result.style = attributes.style;
    }

    return result;
}

/**
 * CKEditor plugin for handling emoji elements
 * 
 * This plugin allows insertion and management of custom emoji elements
 * within the editor content. Emojis are treated as inline objects that
 * can be inserted via commands and are rendered as widgets in the editor.
 */
export class EmojiPlugin extends Plugin {
    /**
     * The plugin name identifier
     */
    public static get pluginName(): string {
        return EMOJI_CONFIG.PLUGIN_NAME;
    }

    /**
     * Initializes the emoji plugin
     * Sets up schema, converters, and commands
     */
    public init(): void {
        this._defineSchema();
        this._defineConverters();
        this._registerCommand();
    }

    /**
     * Defines the schema for emoji elements in the model
     * @private
     */
    private _defineSchema(): void {
        const schema = this.editor.model.schema;
        
        schema.register(ARBO_EMOJI, {
            inheritAllFrom: '$inlineObject',
            allowAttributes: [...EMOJI_CONFIG.ALLOWED_ATTRIBUTES],
        });
    }

    /**
     * Defines the conversion rules between model and view
     * @private
     */
    private _defineConverters(): void {
        const conversion = this.editor.conversion;

        // Upcast: View → Model (when loading content)
        conversion.for('upcast').elementToElement({
            view: ARBO_EMOJI,
            model: (viewElement, { writer }) => {
                const attributes = iteratorToObject(viewElement.getAttributes());
                return writer.createElement(ARBO_EMOJI, attributes);
            },
        });

        // Data downcast: Model → View (when saving content)
        conversion.for('dataDowncast').elementToElement({
            model: ARBO_EMOJI,
            view: (modelElement, { writer }) => {
                const rawAttributes = iteratorToObject(modelElement.getAttributes());
                const attributes = convertToEmojiAttributes(rawAttributes);
                
                return writer.createEmptyElement(ARBO_EMOJI, attributes);
            },
        });

        // Editing downcast: Model → View (for editor display)
        conversion.for('editingDowncast').elementToElement({
            model: ARBO_EMOJI,
            view: (modelElement, { writer }) => {
                const rawAttributes = iteratorToObject(modelElement.getAttributes());
                const attributes = convertToEmojiAttributes(rawAttributes);
                
                // Create the inner emoji element
                const emojiElement = writer.createAttributeElement(ARBO_EMOJI, attributes);
                
                // Wrap in a container for widget functionality
                const containerElement = writer.createContainerElement(
                    'span', 
                    { class: EMOJI_CONFIG.VIEW_CLASS }, 
                    [emojiElement]
                );

                // Convert to widget for editor interaction
                return toWidget(containerElement, writer, {
                    label: EMOJI_CONFIG.WIDGET_LABEL,
                    hasSelectionHandle: false,
                });
            },
        });
    }

    /**
     * Registers the emoji insertion command
     * @private
     */
    private _registerCommand(): void {
        this.editor.commands.add(ARBO_EMOJI, new EmojiCommand(this.editor));
    }
}
